/**
 * MonthCalendar — generic month-grid extracted from ComplianceCalendar so the
 * Meetings page reuses the exact same calendar. Hand-rolled grid (no calendar
 * dependency), theme-aware.
 *
 * The parent owns the selected day (`selected`/`onSelect`) and renders the
 * day-detail panel below the grid; this component owns month navigation.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { SkeletonBlock } from './Skeleton.js';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_CHIPS_PER_DAY = 3;

export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function MonthCalendar<T>({
  items,
  isLoading,
  error,
  dateOf,
  chipKey,
  renderChip,
  selected,
  onSelect,
}: {
  items: T[] | undefined;
  isLoading: boolean;
  error: Error | null;
  /** Calendar day an item sits on; null = not shown. */
  dateOf: (item: T) => Date | null;
  chipKey: (item: T) => string;
  renderChip: (item: T) => ReactNode;
  /** Selected day key (dayKey format) — controlled by the parent. */
  selected: string;
  onSelect: (key: string) => void;
}) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const byDay = useMemo(() => {
    const map = new Map<string, T[]>();
    for (const item of items ?? []) {
      const when = dateOf(item);
      if (!when) continue;
      const key = dayKey(when);
      const arr = map.get(key) ?? [];
      arr.push(item);
      map.set(key, arr);
    }
    return map;
  }, [items, dateOf]);

  if (isLoading) return <SkeletonBlock rows={5} />;
  if (error) return <div className="bg-bg-panel border border-border rounded-xl p-8 text-center text-rose-400 text-sm">{error.message}</div>;

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  // Monday-start offset of the 1st (getDay: 0=Sun … 6=Sat).
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: { date: Date; inMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const dayNum = i - firstDow + 1;
    cells.push({ date: new Date(year, month, dayNum), inMonth: dayNum >= 1 && dayNum <= daysInMonth });
  }

  const monthLabel = cursor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const goMonth = (delta: number) => setCursor(new Date(year, month + delta, 1));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => goMonth(-1)} aria-label="Previous month" className="p-1.5 rounded-lg border border-border text-muted hover:text-white hover:border-accent">
            <ChevronLeft size={16} />
          </button>
          <h2 className="text-sm font-medium text-white min-w-36 text-center">{monthLabel}</h2>
          <button onClick={() => goMonth(1)} aria-label="Next month" className="p-1.5 rounded-lg border border-border text-muted hover:text-white hover:border-accent">
            <ChevronRight size={16} />
          </button>
          <button
            onClick={() => { setCursor(new Date(today.getFullYear(), today.getMonth(), 1)); onSelect(dayKey(today)); }}
            className="text-xs text-muted hover:text-white border border-border hover:border-accent px-2 py-1 rounded"
          >
            Today
          </button>
        </div>
        <div className="text-xs text-muted">{items?.length ?? 0} items</div>
      </div>

      <div className="overflow-x-auto bg-bg-panel border border-border rounded-xl">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-7 border-b border-border">
            {WEEKDAYS.map((d) => (
              <div key={d} className="px-2 py-2 text-xs font-medium text-muted text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map(({ date, inMonth }, i) => {
              const key = dayKey(date);
              const dayItems = byDay.get(key) ?? [];
              const isToday = key === dayKey(today);
              const isSelected = key === selected;
              return (
                <button
                  key={i}
                  onClick={() => onSelect(key)}
                  className={clsx(
                    'min-h-20 sm:min-h-24 border-b border-r border-border/50 p-1.5 text-left align-top transition-colors',
                    (i + 1) % 7 === 0 && 'border-r-0',
                    !inMonth && 'opacity-40',
                    isSelected ? 'bg-bg-hover/60' : 'hover:bg-bg-hover/30',
                  )}
                >
                  <div className={clsx(
                    'text-xs mb-1 inline-flex items-center justify-center w-5 h-5 rounded-full',
                    isToday ? 'bg-accent text-accent-fg font-semibold' : inMonth ? 'text-white' : 'text-muted',
                  )}>
                    {date.getDate()}
                  </div>
                  <div className="space-y-0.5">
                    {dayItems.slice(0, MAX_CHIPS_PER_DAY).map((item) => (
                      <div key={chipKey(item)}>{renderChip(item)}</div>
                    ))}
                    {dayItems.length > MAX_CHIPS_PER_DAY && (
                      <div className="text-[11px] text-accent px-1">+{dayItems.length - MAX_CHIPS_PER_DAY} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
